import {
  JourneyLifecycleEvent,
  Workflow,
  WorkflowStatus,
  buildRuntimeContext,
  isMappedProductionHost,
  normalizeLifecycleEnv,
  resolveRegionFromHost,
  sanitizeWorkflowAttributes,
  safeRun,
  safeWarn,
} from './telemetry';

const w = window as any;

const getEnvVar = (key: string): string => {
  return process.env[key] || (w._env_ && w._env_[key]) || '';
};

const isLocalhost =
  typeof window !== 'undefined' &&
  /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(window.location.origin);

const clientToken = getEnvVar('REACT_APP_DD_CLIENT_TOKEN');
const applicationId = getEnvVar('REACT_APP_DD_APPLICATION_ID');
const shouldInit = !!(clientToken && applicationId && !isLocalhost);

let rumInstance: any = null;

export const getRuntimeTelemetryContext = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const configuredEnv = getEnvVar('REACT_APP_KUBE_NAMESPACE') || getEnvVar('REACT_APP_BACKEND_ENV') || 'dev';
  const region = resolveRegionFromHost(hostname);
  const normalizedEnv = isMappedProductionHost(hostname) ? 'prod' : normalizeLifecycleEnv(configuredEnv);

  return buildRuntimeContext(normalizedEnv, hostname, region);
};

const initRum = async () => {
  if (!shouldInit) return;

  try {
    const { datadogRum } = await import('@datadog/browser-rum');
    const { reactPlugin } = await import('@datadog/browser-rum-react');

    const kubeDeployment = getEnvVar('REACT_APP_KUBE_DEPLOYMENT') || 'dms-internal-frontend';
    const version = getEnvVar('REACT_APP_VERSION');
    const team = getEnvVar('REACT_APP_DD_TEAM');
    const telemetryContext = getRuntimeTelemetryContext();

    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.eu',
      service: kubeDeployment,
      env: telemetryContext.env,
      version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackResources: true,
      trackUserInteractions: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      silentMultipleInit: true,
      plugins: [reactPlugin({ router: false })],
    });

    datadogRum.setGlobalContextProperty('kube_deployment', kubeDeployment);
    datadogRum.setGlobalContextProperty('deployment_env', telemetryContext.env);
    if (telemetryContext.region) {
      datadogRum.setGlobalContextProperty('region', telemetryContext.region);
    }
    if (team) {
      datadogRum.setGlobalContextProperty('team', team);
    }

    rumInstance = datadogRum;
  } catch (e) {
    safeWarn('Datadog RUM init failed:', e);
  }
};

initRum();

export const addRumAction = (
  actionName: string,
  workflow: Workflow,
  status: WorkflowStatus,
  attributes: Record<string, unknown> = {}
) => {
  safeRun(() => {
    if (!rumInstance) return;
    const sanitizedAttributes = sanitizeWorkflowAttributes(workflow, attributes);
    rumInstance.addAction(actionName, {
      workflow,
      status,
      ...sanitizedAttributes,
    });
  });
};

export const addRumError = (error: Error, context?: Record<string, unknown>) => {
  safeRun(() => {
    if (!rumInstance) return;
    rumInstance.addError(error, context);
  });
};

export const addRumJourneyAction = (
  actionName: JourneyLifecycleEvent,
  attributes: Record<string, unknown> = {}
) => {
  safeRun(() => {
    if (!rumInstance) return;
    rumInstance.addAction(actionName, attributes);
  });
};
